import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchMasterMap, savePositions } from '../actions/terrainActions';
import { MasterMapData, Viewport } from '../types/terrain.types';
import { AppState } from '../../../store/store';

const DEFAULT_VIEWPORT: Viewport = { zoom: 1.0, panX: 0, panY: 0 };

interface TerrainState {
  map: MasterMapData | null;
  loading: boolean;
  error: string | null;
  draftPositions: Record<number, { map_x: number; map_y: number }>;
  selectedClearingId: number | null;
  saving: boolean;
  saveError: string | null;
  viewport: Viewport;
  tierFilter: 1 | 2 | 'both';
}

const initialState: TerrainState = {
  map: null,
  loading: false,
  error: null,
  draftPositions: {},
  selectedClearingId: null,
  saving: false,
  saveError: null,
  viewport: DEFAULT_VIEWPORT,
  tierFilter: 'both',
};

const terrainSlice = createSlice({
  name: 'terrain',
  initialState,
  reducers: {
    updateDraftPosition: (
      state,
      action: PayloadAction<{ clearingId: number; map_x: number; map_y: number }>
    ) => {
      const { clearingId, map_x, map_y } = action.payload;
      state.draftPositions[clearingId] = { map_x, map_y };
    },
    setSelectedClearing: (state, action: PayloadAction<number | null>) => {
      state.selectedClearingId = action.payload;
    },
    clearSaveError: (state) => {
      state.saveError = null;
    },
    setViewport: (state, action: PayloadAction<Viewport>) => {
      state.viewport = action.payload;
    },
    resetViewport: (state) => {
      state.viewport = DEFAULT_VIEWPORT;
    },
    setTierFilter: (state, action: PayloadAction<1 | 2 | 'both'>) => {
      state.tierFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterMap.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMasterMap.fulfilled, (state, action) => {
        state.map = action.payload;
        state.loading = false;
      })
      .addCase(fetchMasterMap.rejected, (state) => {
        state.error = 'Failed to load terrain map.';
        state.loading = false;
      })
      .addCase(savePositions.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(savePositions.fulfilled, (state) => {
        if (state.map) {
          state.map.clearings = state.map.clearings.map((c) => {
            const draft = state.draftPositions[c.id];
            return draft ? { ...c, ...draft } : c;
          });
          state.draftPositions = {};
        }
        state.saving = false;
      })
      .addCase(savePositions.rejected, (state) => {
        state.saveError = 'Failed to save positions.';
        state.saving = false;
      });
  },
});

export const {
  updateDraftPosition,
  setSelectedClearing,
  clearSaveError,
  setViewport,
  resetViewport,
  setTierFilter,
} = terrainSlice.actions;

export const selectTerrainMap = (state: AppState) => state.terrain.map;
export const selectTerrainLoading = (state: AppState) => state.terrain.loading;
export const selectTerrainError = (state: AppState) => state.terrain.error;
export const selectDraftPositions = (state: AppState) => state.terrain.draftPositions;
export const selectSelectedClearingId = (state: AppState) => state.terrain.selectedClearingId;
export const selectSaving = (state: AppState) => state.terrain.saving;
export const selectSaveError = (state: AppState) => state.terrain.saveError;
export const selectViewport = (state: AppState) => state.terrain.viewport;
export const selectTierFilter = (state: AppState) => state.terrain.tierFilter;

export default terrainSlice.reducer;
